import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase/client';
import { User } from '@supabase/supabase-js';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from '../utils/useTranslation';

interface UserGroupsCarouselProps {
    user: User | null;
    onGroupClick: (groupId: string, participantId: string) => void;
}

interface GroupData {
    id: string;
    group_id: string;
    participant_id: string;
    group_name: string;
    group_theme: string;
    participant_count: number;
    removed: boolean;
    removed_at?: string;
}

export function UserGroupsCarousel({ user, onGroupClick }: UserGroupsCarouselProps) {
    const [groups, setGroups] = useState<GroupData[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const { t, tVar } = useTranslation();

    useEffect(() => {
        if (user?.email) {
            fetchUserGroups();
        }
    }, [user]);

    const fetchUserGroups = async () => {
        if (!user?.email) return;

        try {
            const { data: participants, error } = await supabase
                .from('participants')
                .select(`
          id,
          group_id,
          removed,
          removed_at,
          groups!inner(
            id,
            name,
            theme
          )
        `)
                .eq('email', user.email.toLowerCase())
                .order('created_at', { ascending: false });

            if (error) throw error;

            if (participants && participants.length > 0) {
                // Para cada grupo, buscar a contagem de participantes
                const groupsData = await Promise.all(
                    participants.map(async (p: any) => {
                        const { count } = await supabase
                            .from('participants')
                            .select('*', { count: 'exact', head: true })
                            .eq('group_id', p.group_id)
                            .eq('removed', false);

                        return {
                            id: p.id,
                            group_id: p.group_id,
                            participant_id: p.id,
                            group_name: p.groups.name,
                            group_theme: p.groups.theme,
                            participant_count: count || 0,
                            removed: p.removed || false,
                            removed_at: p.removed_at
                        };
                    })
                );

                setGroups(groupsData);
            }
        } catch (error) {
            console.error('Error fetching user groups:', error);
        } finally {
            setLoading(false);
        }
    };

    const getThemeEmoji = (theme: string) => {
        const emojis: Record<string, string> = {
            natal: '🎄',
            trabalho: '🏢',
            familia: '👨‍👩‍👧',
            amigos: '🎉',
            zoacao: '😂',
            igreja: '⛪',
            escola: '🎓',
            chocolate: '🍫',
            outros: '🎁',
        };
        return emojis[theme] || '🎁';
    };

    const getThemeGradient = (theme: string) => {
        const gradients: Record<string, string> = {
            natal: 'from-red-500 to-green-500',
            trabalho: 'from-blue-500 to-indigo-500',
            familia: 'from-pink-500 to-purple-500',
            amigos: 'from-yellow-500 to-orange-500',
            zoacao: 'from-green-500 to-teal-500',
            igreja: 'from-purple-600 to-blue-600',
            escola: 'from-amber-500 to-orange-600',
            chocolate: 'from-amber-800 to-yellow-600',
            outros: 'from-gray-500 to-slate-600',
        };
        return gradients[theme] || 'from-red-500 to-green-500';
    };

    const nextGroup = () => {
        setCurrentIndex((prev) => (prev + 1) % groups.length);
    };

    const prevGroup = () => {
        setCurrentIndex((prev) => (prev - 1 + groups.length) % groups.length);
    };

    if (loading) {
        return null;
    }

    if (groups.length === 0) {
        return null;
    }

    const currentGroup = groups[currentIndex];

    return (
        <div className="mb-8">
            <h3 className="text-lg font-semibold text-center mb-4">{t('carousel.title')}</h3>

            <div className="relative max-w-md mx-auto">
                {/* Card do Grupo */}
                <div
                    onClick={() => !currentGroup.removed && onGroupClick(currentGroup.group_id, currentGroup.participant_id)}
                    className={`${currentGroup.removed
                        ? 'bg-gradient-to-r from-gray-400 to-red-400'
                        : `bg-gradient-to-r ${getThemeGradient(currentGroup.group_theme)}`
                        } rounded-2xl p-6 shadow-lg ${currentGroup.removed ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:scale-105'
                        } transition-transform`}
                >
                    <div className="text-center text-white">
                        <div className="text-6xl mb-3">
                            {currentGroup.removed ? '🚫' : getThemeEmoji(currentGroup.group_theme)}
                        </div>
                        <h4 className="text-2xl font-bold mb-2">{currentGroup.group_name}</h4>
                        {currentGroup.removed ? (
                            <div className="bg-white/20 backdrop-blur rounded-lg p-3 mt-3">
                                <p className="text-white font-semibold">{t('carousel.removed.title')}</p>
                                <p className="text-white/80 text-sm mt-1">{t('carousel.removed.subtitle')}</p>
                            </div>
                        ) : (
                            <p className="text-white/90">{tVar('carousel.participantCount', { count: String(currentGroup.participant_count) })}</p>
                        )}
                    </div>
                </div>

                {/* Navegação do Carrossel */}
                {groups.length > 1 && (
                    <>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                prevGroup();
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 shadow-lg hover:bg-white hover:scale-110 transition-all rounded-full z-10"
                        >
                            <ChevronLeft className="h-6 w-6 text-gray-700" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                nextGroup();
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 shadow-lg hover:bg-white hover:scale-110 transition-all rounded-full z-10"
                        >
                            <ChevronRight className="h-6 w-6 text-gray-700" />
                        </Button>

                        {/* Indicadores */}
                        <div className="flex justify-center gap-2 mt-4">
                            {groups.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`h-2 rounded-full transition-all ${index === currentIndex ? 'w-8 bg-purple-600' : 'w-2 bg-gray-300'
                                        }`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
